import { combineReducers, createSlice } from "@reduxjs/toolkit";
import { registerModal } from "../../../libs/modals";
import {
  BULK_CREATE_POST_MEDIA_SELECT_MODAL,
  BULK_CREATE_POST_MODAL,
  requestPostsFromIds,
} from "./bulk-create-post.actions";
import { BulkCreatePostModal } from "./bulk-create-post.modal";
import { MediaSelectModal } from "./media-select.modal";
import { DraftRestorationModal } from "./draft-restoration.modal";
import { useDispatch, useSelector } from "react-redux";
import { ReduxRootState } from "../../../types/redux-store";
import { pullRequestedPostData } from "../components/post-save-success/post-save-success";
import { User } from "../../user/types";
import { useEffect } from "react";

export const DRAFT_RESTORATION_MODAL = "DRAFT_RESTORATION_MODAL";

export const registerBulkCreatePostModals = () => {
  registerModal(BULK_CREATE_POST_MODAL, BulkCreatePostModal);
  registerModal(BULK_CREATE_POST_MEDIA_SELECT_MODAL, MediaSelectModal);
  registerModal(DRAFT_RESTORATION_MODAL, DraftRestorationModal);
};

export const selectedPostIdSlice = createSlice({
  name: "selectedPostId",
  initialState: null as string | null | undefined,
  reducers: {
    set: (_, action: { payload: string | undefined }) => action.payload || null,
  },
});

interface BulkModalPendingState {
  isLoading?: boolean;
  error?: string;
}
export const bulkModalPendingState = createSlice({
  name: "bulkModalPendingState",
  initialState: {} as BulkModalPendingState,
  reducers: {
    setLoading: (state, action: { payload: boolean }) => ({
      ...state,
      isLoading: action.payload,
      error: action.payload ? undefined : state.error,
    }),
    setError: (state, action: { payload: string }) => ({
      ...state,
      isLoading: false,
      error: action.payload,
    }),
  },
});

// Capacity-limit messages surface inside the bulk-create modal as inline
// alerts (CLAUDE.md: plan / capacity limits MUST NOT use toasts — they
// disappear in 5s and the user has no way to discover the cap).
interface BulkModalNoticeState {
  capacityMessage?: string | null;
}
export const bulkModalNotice = createSlice({
  name: "bulkModalNotice",
  initialState: {} as BulkModalNoticeState,
  reducers: {
    setCapacityMessage: (
      state,
      action: { payload: string | null }
    ): BulkModalNoticeState => ({
      ...state,
      capacityMessage: action.payload,
    }),
    clear: (): BulkModalNoticeState => ({}),
  },
});

export const useBulkState = () => {
  return useSelector(
    (state: ReduxRootState) => state["@features/bulk-create-post"]
  );
};

export const bulkModelKey = "@features/bulk-create-post" as const;
export const getBulkModelRootReducer = () => ({
  [bulkModelKey]: getBulkModelReducer(),
});
const getBulkModelReducer = () =>
  combineReducers({
    selectedPostId: selectedPostIdSlice.reducer,
    bulkModalPendingState: bulkModalPendingState.reducer,
    bulkModalNotice: bulkModalNotice.reducer,
  });
export type BulkModelState = ReturnType<ReturnType<typeof getBulkModelReducer>>;

export function useAndOpenRequestedPosts({
  dispatch,
  user,
}: {
  dispatch: ReturnType<typeof useDispatch>;
  user: User;
}) {
  useEffect(() => {
    if (isUserReady(user)) {
      const data = pullRequestedPostData();
      if (!data?.postIds?.length || !`${location}`.includes("open-posts")) {
        return;
      }
      dispatch(
        requestPostsFromIds({ ids: data.postIds, state: data.postState })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}
function isUserReady(user: User) {
  return !!user?.userId;
}
